
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DocumentsService } from '@app/core/services';
import { IAGProductCaseTableConfig } from '@app/products/dmepos/models/ag-product-case-table-config.model';
import { TableColumnConfig } from '@app/shared/models';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { Table, TableModule } from 'primeng/table';
import { ProductLinesSelectionComponent } from '../product-lines-selection/product-lines-selection.component';
import { SelectItem } from 'primeng/api';
import { PanelModule } from 'primeng/panel';

@Component({
    selector: 'mnc-product-cases-selection',
    templateUrl: './product-cases-selection.component.html',
    imports: [
    FormsModule,
    PanelModule,
    TableModule,
    ProductLinesSelectionComponent,
    MultiSelectModule,
      DatePickerModule
]
})
export class ProductCasesSelectionComponent implements OnInit, OnChanges {
  @Input() cases!: object[];
  @Input() filterable!: boolean;
  @Input() multiselect!: boolean;
  @Input() sortable!: boolean;
  @Output() casesLinesSelected = new EventEmitter<object[]>();

  @ViewChild('dataTable', { static: true }) dataTable!: Table;

  tableData: IAGProductCaseTableConfig[] = [];
  columnConfig!: TableColumnConfig[];
  selection: object[] = [];
  activeCase: IAGProductCaseTableConfig;
  lines: object[] = [];

  private readonly documentsService = inject(DocumentsService);
  private readonly ref = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.tableData = this.initTableData();
    this.columnConfig = this.initTableConfig();
  }

  // Need to keep data from the product case widget in sync with this component
  ngOnChanges(changes: SimpleChanges): void {
    const cases = changes['cases']?.currentValue;

    if (cases) {
      this.cases = cases;
      this.tableData = this.initTableData();

      if (this.activeCase) {
        this.activeCase = this.tableData.find(x => x.authorizationCaseId === this.activeCase.authorizationCaseId);
        this.lines = this.activeCase.productLines;
      }
    }
  }

  // Using union type to support single and multiselect
  // target is empty - is multiselect and all items were unselected
  // target is not empty - is multiselect and items were selected
  // target is null - is single select and item was unselected
  // target is not null - is single select and item was selected
  selectionChange(target: IAGProductCaseTableConfig[] | IAGProductCaseTableConfig): void {
    if (target === null) {
      target = [];
    }

    const selectedItem: IAGProductCaseTableConfig[] = !Array.isArray(target) ? [target] : target;
    this.selection = selectedItem;

    // There can be 0 or many selected cases at a time, but only 1 active case to indicate when to show the
    // product line table.
    if (selectedItem.length === 1) {
      this.activeCase = selectedItem[0];
      this.lines = this.activeCase.productLines;
    } else {
      this.activeCase = null;
      this.lines = [];
    }

    // Whenever case selection is made, always reset productLineIds. Will be populated during line selection.
    const caseLinesSelection = selectedItem.map((x) => {
      return {
        authorizationCaseId: x.authorizationCaseId,
        productLineIds: [],
      };
    });

    this.casesLinesSelected.emit(caseLinesSelection);
  }

  // We have a requirement where line selection is only available if exactly 1 case is selected.
  // Expectation is that there is already an entry for the 1 active case. Just update the productLineIds.
  handleLinesSelected(data: string[]): void {
    const caseLinesSelection = [
      {
        authorizationCaseId: this.activeCase.authorizationCaseId,
        productLineIds: data,
      },
    ];
    this.casesLinesSelected.emit(caseLinesSelection);
  }

  clearCaseTableFilters(): void {
    this.dataTable.clear();
    this.columnConfig = this.initTableConfig();
  }

  private initTableData(): IAGProductCaseTableConfig[] {
    return this.cases.map<IAGProductCaseTableConfig>((x) => {
      const pl = x['productLines'];
      return {
        authorizationCaseId: x['authorizationCaseId'],
        caseNumber: x['sequenceNumber'],
        category: x['category']['description'],
        productLineCount: pl.length,
        clinicalQuestionStatus: this.getClinicalQuestionFromLine(pl),
        urgency: x['urgencyDescription'],
        status: x['status']['description'],
        queue: x['queue'],
        productLines: x['productLines'],
      };
    });
  }

  private initTableConfig(): TableColumnConfig[] {
    let caseNumberList: SelectItem[] = []
    let categoryList: SelectItem[] = []
    let statusList: SelectItem[] = []

    if (this.filterable) {
      caseNumberList = this.documentsService.getUniqueFilterOptions(
        this.tableData.map((x) => x.caseNumber)
      );

      categoryList = this.documentsService.getUniqueFilterOptions(
        this.tableData.map((x) => x.category)
      );

      statusList = this.documentsService.getUniqueFilterOptions(
        this.tableData.map((x) => x.status)
      );
    }

    return [
      {
        header: 'Case Number',
        field: 'caseNumber',
        styles: { minWidth: '110px' },
        isSortable: this.sortable,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: caseNumberList,
      },
      {
        header: 'Case Category',
        field: 'category',
        styles: { minWidth: '110px' },
        isSortable: this.sortable,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: categoryList,
      },
      {
        header: 'Quantity of Product Lines in Case',
        field: 'productLineCount',
        styles: { minWidth: '210px' },
        isSortable: this.sortable,
      },
      {
        header: 'Clinical Questions',
        field: 'clinicalQuestionStatus',
        styles: { minWidth: '150px' },
        isSortable: this.sortable,
      },
      {
        header: 'Urgency',
        field: 'urgency',
        styles: { minWidth: '160px' },
        isSortable: this.sortable,
      },
      {
        header: 'Case Status',
        field: 'status',
        styles: { minWidth: '110px' },
        isSortable: this.sortable,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: statusList,
      },
      {
        header: 'Queue',
        field: 'queue',
        styles: { minWidth: '150px' },
        isSortable: this.sortable,
      },
    ];
  }

  private getClinicalQuestionFromLine(productLines: object[]): string {
    return productLines.some((pl) => !pl['formMetadataId'])
      ? 'Pending'
      : 'Complete';
  }
}
